import Bugsnag from "@bugsnag/js";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { gainsight } from "./trackers";
import { useGainsightGlobalContext } from "./trackers/gainsightGlobalContextUtils";
import {
  NECESSARY_COOKIES_CATEGORY_ID,
  shouldAllowAnalytics,
} from "./tracking/onetrust";
import { amWindow } from "./types";

const ONETRUST_SCRIPT_1 = (
  <script
    type="text/javascript"
    src="https://cdn.cookielaw.org/consent/4324426d-de12-4872-8105-5abd4ea95772/OtAutoBlock.js"
  />
);

const ONETRUST_SCRIPT_2 = (
  <script
    src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
    type="text/javascript"
    // eslint-disable-next-line react/no-unknown-property
    charSet="UTF-8"
    data-domain-script="4324426d-de12-4872-8105-5abd4ea95772"
  />
);

const GAINSIGHT_SCRIPT = (
  <script type="text/javascript">
    {`(function(n,t,a,e,co){var i="aptrinsic";n[i]=n[i]||function(){
      (n[i].q=n[i].q||[]).push(arguments)},n[i].p=e;n[i].c=co;
  var r=t.createElement("script");r.async=!0,r.src=a+"?a="+e;
  var c=t.getElementsByTagName("script")[0];c.parentNode.insertBefore(r,c)
  })(window,document,"https://web-sdk.aptrinsic.com/api/aptrinsic.js","${process.env.REACT_APP_GAINSIGHT_TAG}")`}
  </script>
);

const sendConsentChanged = (defaultCategories, enabledCategories) => {
  // If the size of the enabled categories is less than the size of the default categories then we can safely assume the user opted out of a category.
  const optOut = enabledCategories.length < defaultCategories.length;
  const target = `${
    process.env.REACT_APP_API_BASE_URL
  }Ajax/setTrackingOptOut?optOut=${
    optOut ? "1" : "0"
  }&categories=${enabledCategories.join(",")}`;

  const xmlHttp = new XMLHttpRequest();
  xmlHttp.open("POST", target, true);
  xmlHttp.send();
};

const AnalyticsManager = () => {
  const [activeCategories, setActiveCategories] = useState(
    NECESSARY_COOKIES_CATEGORY_ID
  );
  const [isGainsightInitialized, setGainsightInitialized] = useState(false);

  // setup OneTrust.OnConsentChange() event handler
  useEffect(() => {
    // setInterval to run the code again later if OneTrust has not been loaded.
    const initOneTrust = setInterval(() => {
      if (amWindow.OneTrust) {
        if (amWindow.OnetrustActiveGroups) {
          // This timeout fixes GS not always initializing since OneTrust takes a few milliseconds to update window,OnetrustActiveGroups //
          setTimeout(() => {
            // setActiveCategories with actual value from window
            // We need this for cases where default active group includes more tha necessary cookies.
            // E.g. in locations where we do not need to get cookie consent.
            setActiveCategories(amWindow.OnetrustActiveGroups);

            const defaultCategories = amWindow.OnetrustActiveGroups.split(
              ","
            ).filter((category) => category !== "");
            amWindow.OneTrust.OnConsentChanged((event) => {
              sendConsentChanged(defaultCategories, event.detail);
              setActiveCategories(amWindow.OnetrustActiveGroups);
            });
          }, 1000);
        }
        clearInterval(initOneTrust);
      }
    }, 500);

    return () => {
      clearInterval(initOneTrust);
    };
  }, []);

  const userIdentifier = useSelector(
    (state: any) => state.analyticsManager?.userIdentifier
  );
  // Initialize Gainsight if we have Cookie consent for it
  useEffect(() => {
    let initGainsight = null;
    if (shouldAllowAnalytics(activeCategories)) {
      initGainsight = setInterval(() => {
        if (amWindow.aptrinsic) {
          gainsight.identify(userIdentifier);
          clearInterval(initGainsight);
          setGainsightInitialized(true);
        }
      }, 500);
    } else {
      // reset the tracked session.
      // Cannot make gainsight stop sending out analytics event after it's loaded.
      // This will disassociate user with previously identified session.
      gainsight.reset();
    }

    return () => {
      if (initGainsight) {
        clearInterval(initGainsight);
      }
    };
  }, [activeCategories, userIdentifier]);

  useGainsightGlobalContext(isGainsightInitialized);

  // start Bugsnag session only after user has given consent
  useEffect(() => {
    if (shouldAllowAnalytics(activeCategories)) {
      Bugsnag.startSession();
    } else {
      // Prevents further events being attributed to the current session
      Bugsnag.pauseSession();
    }
  }, [activeCategories]);

  return (
    <Helmet>
      {ONETRUST_SCRIPT_1}
      {ONETRUST_SCRIPT_2}
      {shouldAllowAnalytics(activeCategories) ? GAINSIGHT_SCRIPT : null}
    </Helmet>
  );
};

export default AnalyticsManager;
